import { BaseData, Household } from '@sky-distribution/adapter-core-pc-rest-interfaces';
import { DigitalGpSubmission } from '@sky-distribution/digital-gp-submission';
import { CoveragesDetails } from '@sky-distribution/gp-onboarding-generic-submission';
import {
  RateData,
  Submission,
  SubmissionIds,
} from '@sky-distribution/gp-onboarding-policy-service';
import { createMachine, send } from 'xstate';
import { RoutePath } from '../const';
import { getDefaultMachineOptions } from '../const/machineOptions';
import {
  AppStateMachineConfig,
  AppStateMachineContext,
  AppStateMachineEvent,
  AppStateMachineServiceMap,
} from '../types';
import { billingPeriods } from './billingPeriods';
import { submissionOptions } from './config/const';
import { coveragesMeta, digitalCoverages } from './coverages';

export const mockedMachineConfig: AppStateMachineConfig = {
  predictableActionArguments: true,
  preserveActionOrder: true,
  id: 'machine',
  type: 'parallel',
  context: {
    salesPortalButtonEnabled: false,
    productCode: 'hoh',
    language: 'pl',
    rateData: {} as RateData,
    submission: {
      baseData: {} as BaseData,
      variants: {} as Household,
    } as Submission,
    digitalSubmission: {
      baseData: {},
      variants: {},
    } as DigitalGpSubmission,
    digitalCoverages,
    digitalTrackingCodes: {},
    tenant: '',
    coveragesMeta,
    billingPeriods,
    submissionOptions,
    route: '/',
    loading: false,
    ids: {} as SubmissionIds,
    coveragesDetails: {} as CoveragesDetails,
    consents: {},
    consentDictionary: [],
  },
  schema: {
    context: {} as AppStateMachineContext,
    events: {} as AppStateMachineEvent,
    services: {} as AppStateMachineServiceMap,
  },
  on: {
    ERROR: {
      target: '#error.generic',
    },
    NOT_FOUND: {
      target: '#error.notFound',
    },
    CHANGE_ROUTE: {
      actions: 'changeRoute',
    },
  },
  states: {
    onboarding: {
      initial: 'unknown',
      states: {
        unknown: {
          always: [
            { cond: 'isNotFound', target: '#error.notFound' },
            { cond: 'isGenericError', target: '#error.generic' },
            { cond: 'isSuccess', target: '#page.success' },
            { target: '#page.welcome' },
          ],
        },
        page: {
          tags: ['MF:OTP'],
          id: 'page',
          initial: 'welcome',
          states: {
            hist: {
              type: 'history',
            },
            welcome: {
              tags: ['welcome'],
              meta: {
                stepTitle: '1 z 3',
                stepValue: 1,
                stepMaxValue: 4,
              },
              entry: send({
                type: 'CHANGE_ROUTE',
                data: {
                  routePath: RoutePath.Home,
                },
              }),
              on: {
                NEXT: {
                  actions: 'saveData',
                  target: '#loader.loading',
                },
              },
            },
            offers: {
              tags: 'offer',
              meta: {
                stepTitle: '2 z 3',
                stepValue: 2,
                stepMaxValue: 4,
              },
              entry: send({
                type: 'CHANGE_ROUTE',
                data: {
                  routePath: RoutePath.Offers,
                },
              }),
              on: {
                NEXT: {
                  actions: 'saveData',
                  target: '#page.summary',
                },
                QUOTE: {
                  actions: 'saveData',
                  target: '#loader.refresh',
                },
                PREVIOUS: {
                  target: '#page.welcome',
                },
                GO_WELCOME: {
                  target: '#page.welcome',
                },
              },
            },
            summary: {
              tags: 'summaryScreen',
              meta: {
                stepTitle: '3 z 3',
                stepValue: 3,
                stepMaxValue: 4,
              },
              entry: send({
                type: 'CHANGE_ROUTE',
                data: {
                  routePath: RoutePath.Summary,
                },
              }),
              on: {
                PREVIOUS: {
                  target: '#page.offers',
                },
                GO_WELCOME: {
                  target: '#page.welcome',
                },
                NEXT: [
                  {
                    cond: 'isPolicyDraft',
                    actions: 'saveData',
                    target: '#loader.refresh',
                  },
                  { actions: 'saveData', target: '#loader.redirect' },
                ],
              },
            },
            payment: {
              tags: 'payment',
              entry: 'redirectToPayment',
              on: {
                PREVIOUS: {
                  target: '#page.summary',
                },
              },
            },
            success: {
              tags: 'success',
              type: 'final',
              entry: send({
                type: 'CHANGE_ROUTE',
                data: {
                  routePath: RoutePath.ThankYou,
                },
              }),
              meta: {
                stepTitle: 'Gotowe',
                stepValue: 1,
                stepMaxValue: 1,
              },
            },
          },
        },
        error: {
          id: 'error',
          initial: 'generic',
          states: {
            generic: {
              tags: ['error', 'generic'],
              entry: send({
                type: 'CHANGE_ROUTE',
                data: {
                  routePath: RoutePath.Error,
                },
              }),
              on: {
                PREVIOUS: {
                  target: '#page.hist',
                },
              },
            },
            notFound: {
              tags: ['error', 'notFound'],
              entry: [
                send({
                  type: 'CHANGE_ROUTE',
                  data: {
                    routePath: RoutePath.NotFound,
                  },
                }),
              ],
              on: {
                PREVIOUS: {
                  target: '#page.welcome',
                },
              },
            },
            cantInsure: {
              tags: ['error', 'cantInsure'],
              entry: send({
                type: 'CHANGE_ROUTE',
                data: {
                  routePath: RoutePath.CantInsure,
                },
              }),
              on: {
                PREVIOUS: {
                  target: '#page.hist',
                },
              },
            },
          },
        },
        loader: {
          id: 'loader',
          states: {
            loading: {
              tags: ['loading', 'create'],
              invoke: {
                id: 'createService',
                src: 'createService',
                onDone: {
                  actions: 'saveResponseData',
                  target: ['#page.offers', '#policy.quoted'],
                },
                onError: [
                  {
                    cond: 'cantInsure',
                    target: '#error.cantInsure',
                  },
                  {
                    target: ['#error.generic', '#policy.initial'],
                  },
                ],
              },
            },
            refresh: {
              tags: ['busy'],
              invoke: {
                id: 'quoteService',
                src: 'quoteService',
                onDone: {
                  actions: 'saveResponseData',
                  target: ['#page.hist', '#policy.quoted'],
                },
                onError: [
                  {
                    cond: 'cantInsure',
                    target: '#error.cantInsure',
                  },
                  {
                    target: ['#error.generic', '#policy.draft'],
                  },
                ],
              },
            },
            redirect: {
              tags: ['loading', 'buy'],
              invoke: {
                id: 'buyService',
                src: 'buyService',
                onDone: {
                  actions: ['redirectToPayment'],
                  target: ['#policy.proposed'],
                },
                onError: {
                  target: ['#error.generic', '#policy.draft'],
                },
              },
            },
          },
        },
      },
    },
    policy: {
      id: 'policy',
      initial: 'initial',
      states: {
        initial: {
          on: {
            POLICY_CREATED: {
              target: 'quoted',
            },
          },
        },
        quoted: {
          tags: ['quoted'],
          on: {
            POLICY_DRAFT: {
              target: 'draft',
            },
          },
        },
        draft: {
          tags: ['draft'],
          on: {
            POLICY_QUOTED: {
              target: 'quoted',
            },
          },
        },
        proposed: {},
      },
    },
  },
};

export const householdMachine = createMachine(
  mockedMachineConfig,
  getDefaultMachineOptions('/', mockedMachineConfig.context as AppStateMachineContext),
);
